import { useMutation, useSuspenseQuery } from '@apollo/client';
import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { eluveAdminHasuraContext } from '@eluve/apollo-client';
import {
  Button,
  Grid,
  Heading,
  Icon,
  Input,
  Label,
  Stack,
  Switch,
  Text,
  useToast,
} from '@eluve/components';

import {
  createTemplateBundleMutation,
  getTemplateBundleCreateOptionsQuery,
} from './template-bundle.operations';

export const CreateTemplateBundlePage: React.FC = () => {
  const navigate = useNavigate();
  const toast = useToast();

  const {
    data: { promptTemplates, llmModelArgs, llmOutputTemplates },
  } = useSuspenseQuery(getTemplateBundleCreateOptionsQuery, {
    context: eluveAdminHasuraContext,
  });

  const [displayName, setDisplayName] = useState('');
  const [promptTemplateId, setPromptTemplateId] = useState('');
  const [modelArgsId, setModelArgsId] = useState('');
  const [outputTemplateId, setOutputTemplateId] = useState('');
  const [isActive, setIsActive] = useState(true);

  const [promptSearch, setPromptSearch] = useState('');
  const [modelSearch, setModelSearch] = useState('');
  const [outputSearch, setOutputSearch] = useState('');

  const filteredPrompts = useMemo(
    () =>
      promptTemplates.filter((pt) =>
        pt.name.toLowerCase().includes(promptSearch.toLowerCase()),
      ),
    [promptTemplates, promptSearch],
  );

  const filteredModels = useMemo(
    () =>
      llmModelArgs.filter(
        (ma) =>
          (ma.name ?? ma.modelType)
            .toLowerCase()
            .includes(modelSearch.toLowerCase()) ||
          ma.modelType.toLowerCase().includes(modelSearch.toLowerCase()),
      ),
    [llmModelArgs, modelSearch],
  );

  const filteredOutputs = useMemo(
    () =>
      llmOutputTemplates.filter((ot) =>
        ot.name.toLowerCase().includes(outputSearch.toLowerCase()),
      ),
    [llmOutputTemplates, outputSearch],
  );

  const [createBundle, { loading }] = useMutation(
    createTemplateBundleMutation,
    {
      context: eluveAdminHasuraContext,
      refetchQueries: ['getAllTemplateBundles'],
      onCompleted: (data) => {
        const id = data.insertLlmModelPromptTemplatesOne?.id;
        toast.success('Template bundle created');
        navigate(
          id ? `/admin/template-bundles/${id}` : '/admin/template-bundles',
        );
      },
      onError: (error) => {
        if (
          error.message.includes('unique') ||
          error.message.includes('Uniqueness')
        ) {
          toast.error(
            'This combination of prompt, model, and output template already exists',
          );
        } else {
          toast.error(`Failed to create template bundle: ${error.message}`);
        }
      },
    },
  );

  const handleSubmit = () => {
    createBundle({
      variables: {
        promptTemplateId,
        modelArgsId,
        outputTemplateId,
        displayName,
        isActive,
      },
    });
  };

  const isValid =
    displayName.trim() && promptTemplateId && modelArgsId && outputTemplateId;

  const selectedItemClass =
    'border-border-positive-subtle bg-bg-lightgreen-subtle' as const;
  const unselectedItemClass =
    'border-border-secondary bg-bg-secondary hover:border-border-tertiary hover:bg-bg-tertiary' as const;

  return (
    <Stack gap={6}>
      <Stack gap={2} direction="horizontal">
        <div className="my-2 py-2">
          <Heading className="whitespace-nowrap">
            Create Template Bundle
          </Heading>
        </div>

        <Stack gap={2} justify="end" direction="horizontal">
          <Button onClick={handleSubmit} disabled={!isValid || loading}>
            {loading ? 'Creating...' : 'Create Bundle'}
          </Button>
          <Button
            variant="outline"
            onClick={() => navigate('/admin/template-bundles')}
          >
            Cancel
          </Button>
        </Stack>
      </Stack>

      <Stack gap={1}>
        <Label htmlFor="displayName">Display Name *</Label>
        <Input
          id="displayName"
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
          placeholder="Enter a display name"
        />
      </Stack>

      <Stack gap={2} align="center" direction="horizontal">
        <Label htmlFor="isActive">Active</Label>
        <Switch
          id="isActive"
          checked={isActive}
          onCheckedChange={setIsActive}
          size="sm"
        />
      </Stack>

      <Grid columns={1} gap={4} className="h-[600px] md:grid-cols-3">
        {/* Prompt Templates Column */}
        <Stack gap={2} align="stretch" className="min-h-0 overflow-hidden">
          <Label>Prompt Template *</Label>
          <Input
            className="focus-visible:ring-0! focus-visible:ring-offset-0!"
            placeholder="Search prompts..."
            value={promptSearch}
            onChange={(e) => setPromptSearch(e.target.value)}
          />
          <Stack
            gap={1}
            align="stretch"
            className="min-h-0 flex-1 overflow-y-auto"
          >
            {filteredPrompts.map((pt) => (
              <button
                key={pt.id}
                type="button"
                onClick={() => setPromptTemplateId(pt.id)}
                className={`relative cursor-pointer rounded-md border p-3 text-left transition-colors ${
                  promptTemplateId === pt.id
                    ? selectedItemClass
                    : unselectedItemClass
                }`}
              >
                {promptTemplateId === pt.id && (
                  <Icon
                    name="Check"
                    size="lg"
                    className="absolute top-2 right-2 text-text-positive"
                  />
                )}
                <Text inline size="xs" weight="semibold">
                  {pt.name}
                </Text>
                {pt.description && (
                  <Text
                    inline
                    color="tertiary"
                    size="xs"
                    className="mt-1 line-clamp-2"
                  >
                    {pt.description}
                  </Text>
                )}
              </button>
            ))}
            {filteredPrompts.length === 0 && (
              <Text color="tertiary" className="p-4 text-center">
                No prompts found
              </Text>
            )}
          </Stack>
        </Stack>

        {/* Output Templates Column */}
        <Stack gap={2} align="stretch" className="min-h-0 overflow-hidden">
          <Label>Output Template *</Label>
          <Input
            className="focus-visible:ring-0! focus-visible:ring-offset-0!"
            placeholder="Search output templates..."
            value={outputSearch}
            onChange={(e) => setOutputSearch(e.target.value)}
          />
          <Stack
            gap={1}
            align="stretch"
            className="min-h-0 flex-1 overflow-y-auto"
          >
            {filteredOutputs.map((ot) => (
              <button
                key={ot.id}
                type="button"
                onClick={() => setOutputTemplateId(ot.id)}
                className={`relative cursor-pointer rounded-md border p-3 text-left transition-colors ${
                  outputTemplateId === ot.id
                    ? selectedItemClass
                    : unselectedItemClass
                }`}
              >
                {outputTemplateId === ot.id && (
                  <Icon
                    name="Check"
                    size="lg"
                    className="absolute top-2 right-2 text-text-positive"
                  />
                )}
                <Text inline size="xs" weight="semibold">
                  {ot.name}
                </Text>
                <Text inline color="tertiary" size="xs" className="mt-0.5">
                  Output Type: {ot.llmOutputType}
                </Text>
                {ot.description && (
                  <Text
                    inline
                    color="tertiary"
                    size="xs"
                    className="mt-1 line-clamp-2"
                  >
                    {ot.description}
                  </Text>
                )}
              </button>
            ))}
            {filteredOutputs.length === 0 && (
              <Text color="tertiary" className="p-4 text-center">
                No output templates found
              </Text>
            )}
          </Stack>
        </Stack>

        {/* Model Args Column */}
        <Stack gap={2} align="stretch" className="min-h-0 overflow-hidden">
          <Label>Model *</Label>
          <Input
            className="focus-visible:ring-0! focus-visible:ring-offset-0!"
            placeholder="Search models..."
            value={modelSearch}
            onChange={(e) => setModelSearch(e.target.value)}
          />
          <Stack
            gap={1}
            align="stretch"
            className="min-h-0 flex-1 overflow-y-auto"
          >
            {filteredModels.map((ma) => (
              <button
                key={ma.id}
                type="button"
                onClick={() => setModelArgsId(ma.id)}
                className={`relative cursor-pointer rounded-md border p-3 text-left transition-colors ${
                  modelArgsId === ma.id
                    ? selectedItemClass
                    : unselectedItemClass
                }`}
              >
                {modelArgsId === ma.id && (
                  <Icon
                    name="Check"
                    size="lg"
                    className="absolute top-2 right-2 text-text-positive"
                  />
                )}
                <Text inline size="xs" weight="semibold">
                  {ma.name ?? ma.modelType}
                </Text>
                <Text inline color="tertiary" size="xs" className="mt-0.5">
                  Model Type: {ma.modelType}
                </Text>
                {ma.description && (
                  <Text
                    inline
                    color="tertiary"
                    size="xs"
                    className="mt-1 line-clamp-2"
                  >
                    {ma.description}
                  </Text>
                )}
                {ma.args && (
                  <pre className="mt-1 max-h-20 overflow-hidden rounded-sm bg-bg-quaternary p-1.5 text-[10px] text-ellipsis text-text-secondary">
                    {JSON.stringify(ma.args, null, 2)}
                  </pre>
                )}
              </button>
            ))}
            {filteredModels.length === 0 && (
              <Text color="tertiary" className="p-4 text-center">
                No models found
              </Text>
            )}
          </Stack>
        </Stack>
      </Grid>
    </Stack>
  );
};
